import { useCallback, useEffect, useMemo, useState } from 'react';
import MobileStep from './lib/MobileStep';
import CustomOrderMobileHeader from './lib/CustomOrderMobileHeader';
import { Button, Col, Divider, Flex, Form, Input, InputNumber, Row, Select } from 'antd';
import { API } from '../../api';
import { notifyError, notifySuccessTitle, priceFormat } from '../../store/utils';
import { Link } from 'react-router-dom';
import { DeleteOutlined, MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import {
  districtNos,
  emitter,
  getStoreOrders,
  provinceNos,
  setStoreOrders,
} from '../../store/StoreUtils';

const defaultPrice = 5000;
const defaultWeightTypeNo = 1;
const defaultWarningNo = 1;
const defaultDeliveryType = 'HOMEDELIVERY';

interface ItemInterface {
  itemType: string;
  warningNo: number;
  weightTypeNo: number;
  quantity: number;
  deliveryCost: number;
  additionalCost: number;
  weight: number;
}

const initialItemValue: ItemInterface = {
  itemType: '',
  warningNo: 1,
  weightTypeNo: 1,
  quantity: 1,
  deliveryCost: defaultPrice,
  additionalCost: 0,
  weight: 0,
};

interface FormData {
  customer: any | null;
  warningList: any[];
  weightTypeList: any[];
  paymentTypeList: any[];
  deliveryTypeList: any[];
}

const MobileOrderCreate = () => {
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [addressList, setAddressList] = useState<any[]>([]);
  const [address, setAddress] = useState<any>(null);
  const [items, setItems] = useState<any[]>([]);

  const [data, setData] = useState<FormData>({
    customer: null,
    warningList: [],
    weightTypeList: [],
    paymentTypeList: [],
    deliveryTypeList: [],
  });

  const itemsBack = useCallback(() => {
    if (step >= 1) {
      setStep(step - 1);
    }
  }, [step]);

  const fetchFormData = useCallback(async () => {
    try {
      const { result } = await API.get({ apiVersion: 'api' })(`/order/formdata`);
      setData({
        customer: null,
        warningList: result.warningList,
        weightTypeList: result?.weightTypeList,
        paymentTypeList: result?.paymentTypeList || [],
        deliveryTypeList: result?.deliveryTypeList || [],
      });
    } catch (error: any) {
      notifyError(error.message);
    }
  }, []);

  const fetchFullAddress = useCallback(async () => {
    try {
      const { result } = await API.get({ apiVersion: 'api' })(`/address/full`);
      setAddressList(result?.data || []);
    } catch (error: any) {
      notifyError(error.message);
    }
  }, []);

  const itemsSave = useCallback(
    async (list: any[]) => {
      setLoading(true);
      const weightTypeNoList = list.map((item: any) => item.weightTypeNo);
      const payload = {
        customerNo: null,
        senderStreetNo: address.senderStreetNo,
        receiverStreetNo: address.receiverStreetNo,
        weightTypeNoList,
        deliveryType: defaultDeliveryType,
      };

      const { result } = await API.post({ apiVersion: 'api' })(`/order/calculate/cost`, payload);
      const newItems = list.map((item: any) => {
        const price = result.find((p: any) => p.weightTypeNo == item.weightTypeNo) || {
          amount: defaultPrice,
        };

        const weightType = (data?.weightTypeList || []).find(
          (itm: any) => itm.no == item.weightTypeNo
        );
        const warning = (data?.warningList || []).find((itm: any) => itm.no === item.warningNo);

        return {
          ...item,
          weightTypeName: weightType?.name || '',
          warningName: warning?.name || '',
          deliveryCost: price?.amount,
        };
      });
      console.log(newItems);
      setItems(newItems);
      setStep(2);
      try {
      } catch (error: any) {
        notifyError(error.message);
      } finally {
        setLoading(false);
      }
    },
    [address, data]
  );

  const addItem = useCallback(async () => {
    const orders = await getStoreOrders();
    const senderZipcodeNames: string[] = [];
    const receiverZipcodeNames: string[] = [];
    addressList.forEach((provinces: any) => {
      if (provinces.no === address.senderProvinceNo) {
        senderZipcodeNames.push(provinces?.name);
        (provinces.districts || []).forEach((district: any) => {
          if (district.no === address.senderDistrictNo) {
            senderZipcodeNames.push(district?.name);
            (district.streets || []).forEach((street: any) => {
              if (street.no === address.senderStreetNo) {
                senderZipcodeNames.push(street?.name);
              }
            });
          }
        });
      }
      if (provinces.no === address.receiverProvinceNo) {
        receiverZipcodeNames.push(provinces?.name);
        (provinces.districts || []).forEach((district: any) => {
          if (district.no === address.receiverDistrictNo) {
            receiverZipcodeNames.push(district?.name);
            (district.streets || []).forEach((street: any) => {
              if (street.no === address.receiverStreetNo) {
                receiverZipcodeNames.push(street?.name);
              }
            });
          }
        });
      }
    });

    let payload: any = {
      deliveryType: defaultDeliveryType,
      targetDate: null,

      customerNo: null,
      senderFname: address.senderFname,
      senderPhone1: address.senderPhone1,
      senderPhone2: address.senderPhone2,
      senderDetail: address.senderDetail,
      senderW3w: address.senderW3w,
      senderStreetNo: [address.senderProvinceNo, address.senderDistrictNo, address.senderStreetNo],
      senderZipcodeNames,

      receiverFname: address?.receiverFname,
      receiverPhone1: address?.receiverPhone1,
      receiverPhone2: address?.receiverPhone2 || undefined,
      receiverDetail: address?.receiverDetail,
      receiverW3w: address?.receiverW3w,
      receiverStreetNo: [
        address?.receiverProvinceNo,
        address.receiverDistrictNo,
        address.receiverStreetNo,
      ],
      receiverZipcodeNames,
      paymentTypeCd: 'PRE_PAID',
    };

    payload.items = items;

    orders.push(payload);

    await setStoreOrders(orders);

    setItems([initialItemValue]);

    emitter.emit('localOrderChange');

    notifySuccessTitle('Захиалгыг амжилттай хадгаллаа', 'Дараагийн захиалгаа оруулна уу');
    setAddress({
      senderFname: address.senderFname,
      senderPhone1: address.senderPhone1,
      senderPhone2: address.senderPhone2,
      senderDetail: address.senderDetail,
      senderW3w: address.senderW3w,
      senderProvinceNo: address.senderProvinceNo,
      senderDistrictNo: address.senderDistrictNo,
      senderStreetNo: address.senderStreetNo,
    });
    setItems([initialItemValue]);
    setStep(0);
  }, [address, items, addressList]);

  const goPaid = useCallback(() => {
    let payload: any = {
      deliveryType: defaultDeliveryType,
      targetDate: null,

      customerNo: null,
      senderFname: address.senderFname,
      senderPhone1: address.senderPhone1,
      senderPhone2: address.senderPhone2,
      senderDetail: address.senderDetail,
      senderW3w: address.senderW3w,
      senderStreetNo: address.senderStreetNo,

      receiverFname: address?.receiverFname,
      receiverPhone1: address?.receiverPhone1,
      receiverPhone2: address?.receiverPhone2 || undefined,
      receiverDetail: address?.receiverDetail,
      receiverW3w: address?.receiverW3w,
      receiverStreetNo: address.receiverStreetNo,
      paymentTypeCd: 'PRE_PAID',
    };

    payload.items = items;
    console.log(payload);
  }, [address, items]);

  useEffect(() => {
    fetchFormData();
    fetchFullAddress();
  }, []);

  return (
    <div className="mobile-container">
      <CustomOrderMobileHeader step={step} itemsBack={itemsBack} />
      {step === 0 ? (
        <Address
          setValues={(values: any) => {
            setStep(1);
            setAddress(values);
          }}
          addresses={addressList}
          value={address}
        />
      ) : null}
      {step === 1 ? (
        <Items
          data={data}
          loading={loading}
          setItems={(value: any[]) => itemsSave(value)}
          value={[initialItemValue]}
          itemsBack={() => itemsBack()}
        />
      ) : null}
      {step === 2 ? <Payment goPaid={goPaid} addItem={addItem} items={items} /> : null}
    </div>
  );
};

const Address = ({
  setValues,
  value,
  addresses,
}: {
  setValues: (value: any) => void;
  value: any;
  addresses: any[];
}) => {
  const [form] = Form.useForm();

  const onFinish = useCallback(
    (values: any) => {
      setValues(values);
    },
    [form]
  );

  useEffect(() => {
    if (value) {
      form.setFieldsValue({ ...value });
    }
  }, [value]);

  const senderProvinceOptions = useMemo(() => {
    return addresses.map((item: any) => {
      return {
        label: item.name,
        value: item.no,
        disabled: provinceNos.indexOf(item.no) < 0,
      };
    });
  }, [addresses]);

  const senderDistrictOptions = () => {
    const proviceNo = form.getFieldValue('senderProvinceNo');
    const provice: any = addresses.find((itm: any) => itm.no === proviceNo);
    return (provice?.districts || []).map((item: any) => {
      return {
        label: item.name,
        value: item.no,
        disabled: districtNos.indexOf(item.no) < 0,
      };
    });
  };

  const senderStreetOptions = () => {
    const proviceNo = form.getFieldValue('senderProvinceNo');
    const districtNo = form.getFieldValue('senderDistrictNo');
    const provice: any = addresses.find((itm: any) => itm.no === proviceNo);
    const district = (provice?.districts || []).find((itm: any) => itm.no === districtNo);
    return (district?.streets || []).map((item: any) => {
      return {
        label: item.name,
        value: item.no,
      };
    });
  };

  const receiverProvinceOptions = useMemo(() => {
    return addresses.map((item: any) => {
      return {
        label: item.name,
        value: item.no,
      };
    });
  }, [addresses]);

  const receiverDistrictOptions = () => {
    const proviceNo = form.getFieldValue('receiverProvinceNo');
    const provice: any = addresses.find((itm: any) => itm.no === proviceNo);
    return (provice?.districts || []).map((item: any) => {
      return {
        label: item.name,
        value: item.no,
      };
    });
  };

  const receiverStreetOptions = () => {
    const proviceNo = form.getFieldValue('receiverProvinceNo');
    const districtNo = form.getFieldValue('receiverDistrictNo');
    const provice: any = addresses.find((itm: any) => itm.no === proviceNo);
    const district = (provice?.districts || []).find((itm: any) => itm.no === districtNo);
    return (district?.streets || []).map((item: any) => {
      return {
        label: item.name,
        value: item.no,
      };
    });
  };

  return (
    <div style={{ background: '#FFF' }}>
      <MobileStep current={0} />
      <div style={{ padding: 24 }}>
        <Form form={form} onFinish={onFinish} layout="vertical">
          <Form.Item
            label="Илгээгч нэр"
            name="senderFname"
            rules={[{ required: true, message: 'Нэр оруулна уу!' }]}
          >
            <Input placeholder="Илгээгч нэр" />
          </Form.Item>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="Утас"
                name="senderPhone1"
                rules={[{ required: true, message: 'Утас 1 оруулна уу!' }]}
              >
                <Input placeholder="Утас 1" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Утас 2" name="senderPhone2">
                <Input placeholder="Утас 2" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            label="Илгээх хаяг"
            name="senderProvinceNo"
            rules={[{ required: true, message: 'Аймаг, хот сонгоно уу!' }]}
          >
            <Select
              onChange={() => {
                form.setFieldsValue({
                  senderDistrictNo: undefined,
                  senderStreetNo: undefined,
                });
              }}
              placeholder="Аймаг, хот сонгох"
              options={senderProvinceOptions}
            />
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.senderProvinceNo !== currentValues.senderProvinceNo
            }
          >
            {({ getFieldValue }) =>
              getFieldValue('senderProvinceNo') ? (
                <Form.Item
                  name="senderDistrictNo"
                  rules={[{ required: true, message: 'Cум, дүүрэг сонгоно уу!' }]}
                >
                  <Select
                    onChange={() => {
                      form.setFieldsValue({
                        senderStreetNo: undefined,
                      });
                    }}
                    placeholder="Cум, дүүрэг сонгох"
                    options={senderDistrictOptions()}
                  />
                </Form.Item>
              ) : (
                <Form.Item>
                  <Select placeholder="Cум, дүүрэг сонгох" disabled />
                </Form.Item>
              )
            }
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.senderDistrictNo !== currentValues.senderDistrictNo
            }
          >
            {({ getFieldValue }) =>
              getFieldValue('senderDistrictNo') ? (
                <Form.Item
                  name="senderStreetNo"
                  rules={[{ required: true, message: 'Баг, хороо сонгоно уу!' }]}
                >
                  <Select placeholder="Баг, хороо сонгох" options={senderStreetOptions()} />
                </Form.Item>
              ) : (
                <Form.Item>
                  <Select placeholder="Баг, хороо сонгох" disabled />
                </Form.Item>
              )
            }
          </Form.Item>
          <Form.Item
            label="Хаяг дэлгэрэнгүй"
            rules={[{ required: true, message: 'Хаяг дэлгэрэнгүй оруулна уу!' }]}
            name="senderDetail"
          >
            <Input.TextArea rows={3} placeholder="Илгээгчийн дэлгэрэнгүй хаяг" />
          </Form.Item>
          <Divider style={{ marginTop: 15, marginBottom: 15 }} />
          <Form.Item
            label="Хүлээн авагч"
            style={{ marginBottom: 10 }}
            name="receiverFname"
            rules={[{ required: true, message: 'Нэр оруулна уу!' }]}
          >
            <Input placeholder="Хүлээн авагчийн нэр" />
          </Form.Item>
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="Утас"
                rules={[{ required: true, message: 'Утас 1 оруулна уу!' }]}
                name="receiverPhone1"
              >
                <Input placeholder="Утас 1" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Утас 2" name="receiverPhone2">
                <Input placeholder="Утас 2" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            label="Хүлээн авагч хаяг"
            rules={[{ required: true, message: 'Аймаг, хот сонгоно уу!' }]}
            name="receiverProvinceNo"
          >
            <Select
              placeholder="Аймаг, хот сонгох"
              onChange={() => {
                form.setFieldsValue({
                  receiverDistrictNo: undefined,
                  receiverStreetNo: undefined,
                });
              }}
              options={receiverProvinceOptions}
            />
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.receiverProvinceNo !== currentValues.receiverProvinceNo
            }
          >
            {({ getFieldValue }) =>
              getFieldValue('receiverProvinceNo') ? (
                <Form.Item
                  name="receiverDistrictNo"
                  rules={[{ required: true, message: 'Cум, дүүрэг сонгоно уу!' }]}
                >
                  <Select
                    onChange={() => {
                      form.setFieldsValue({
                        receiverStreetNo: undefined,
                      });
                    }}
                    placeholder="Cум, дүүрэг сонгох"
                    options={receiverDistrictOptions()}
                  />
                </Form.Item>
              ) : (
                <Form.Item>
                  <Select disabled placeholder="Cум, дүүрэг сонгох" />
                </Form.Item>
              )
            }
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.receiverDistrictNo !== currentValues.receiverDistrictNo
            }
          >
            {({ getFieldValue }) =>
              getFieldValue('receiverDistrictNo') ? (
                <Form.Item
                  name="receiverStreetNo"
                  rules={[{ required: true, message: 'Cум, дүүрэг сонгоно уу!' }]}
                >
                  <Select placeholder="Cум, дүүрэг сонгох" options={receiverStreetOptions()} />
                </Form.Item>
              ) : (
                <Form.Item>
                  <Select disabled placeholder="Баг, хороо сонгох" />
                </Form.Item>
              )
            }
          </Form.Item>
          <Form.Item
            label="Хаяг дэлгэрэнгүй"
            rules={[{ required: true, message: 'Хаяг дэлгэрэнгүй оруулна уу!' }]}
            name="receiverDetail"
          >
            <Input.TextArea rows={3} placeholder="Илгээгчийн дэлгэрэнгүй хаяг" />
          </Form.Item>
          <Flex gap={24} justify="space-between">
            <Link to="/" style={{ width: '100%' }}>
              <Button style={{ width: '100%' }}>Болих</Button>
            </Link>
            <Button type="primary" htmlType="submit" style={{ width: '100%' }}>
              Үргэлжлүүлэх
            </Button>
          </Flex>
        </Form>
      </div>
    </div>
  );
};

const Items = ({
  itemsBack,
  value,
  setItems,
  data,
  loading,
}: {
  itemsBack: () => void;
  value: any[];
  setItems: (values: any[]) => void;
  data: FormData;
  loading: boolean;
}) => {
  const [form] = Form.useForm();

  const onFinish = useCallback((values: any) => {
    setItems(values.items || []);
  }, []);

  const warningList = useMemo(() => {
    return (data?.warningList || []).map((item: any) => {
      return {
        label: item.name,
        value: item.no,
      };
    });
  }, [data]);

  const weightTypeList = useMemo(() => {
    return (data?.weightTypeList || []).map((item: any) => {
      return {
        label: item.name,
        value: item.no,
      };
    });
  }, [data]);

  return (
    <div style={{ background: '#FFF' }}>
      <MobileStep current={1} />
      <div style={{ padding: 24 }}>
        <Form initialValues={{ items: value }} form={form} onFinish={onFinish} layout="vertical">
          <Form.List name="items">
            {(fields, { add, remove }, { errors }) => (
              <Row>
                {fields.map(({ key, name, ...restField }, index: number) => (
                  <Col key={`item_${key}`} span={24}>
                    <div className="mobile-item-header">
                      <Form.Item
                        {...restField}
                        name={[name, 'itemType']}
                        rules={[{ required: true, message: 'ХИлгээмжийн нэр, төрөл оруулна уу!' }]}
                        label="Илгээмжийн нэр, төрөл"
                      >
                        <Input placeholder="Илгээмжийн нэр, төрөл" />
                      </Form.Item>
                      {fields.length > 1 ? (
                        <Button
                          className="delete"
                          danger
                          type="primary"
                          icon={<DeleteOutlined style={{ fontSize: 14 }} />}
                          size="small"
                          onClick={() => remove(name)}
                        ></Button>
                      ) : null}
                    </div>
                    <Row gutter={8}>
                      <Col span={12}>
                        <Form.Item {...restField} name={[name, 'warningNo']} label="Анхаарах">
                          <Select placeholder="Анхаарах" options={warningList} />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="Хэмжээ" {...restField} name={[name, 'weightTypeNo']}>
                          <Select options={weightTypeList} />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={8}>
                      <Col span={12}>
                        <Form.Item label="Тоо.ш" {...restField} name={[name, 'quantity']}>
                          <InputNumber style={{ width: '100%' }} min={1} step={1} />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item label="Жин (кг)" {...restField} name={[name, 'weight']}>
                          <InputNumber style={{ width: '100%' }} min={0} step={100} />
                        </Form.Item>
                      </Col>
                    </Row>
                    {fields.length - 1 !== index ? (
                      <Divider style={{ marginTop: 10, marginBottom: 15 }} />
                    ) : null}
                  </Col>
                ))}
                <Form.Item>
                  <Button
                    style={{ padding: 0 }}
                    type="link"
                    onClick={() => add()}
                    icon={<PlusOutlined />}
                  >
                    Илгээмж нэмэх
                  </Button>
                </Form.Item>
              </Row>
            )}
          </Form.List>
          <Flex gap={24} justify="space-between">
            <Button onClick={() => itemsBack()} style={{ width: '100%' }}>
              Болих
            </Button>
            <Button loading={loading} type="primary" htmlType="submit" style={{ width: '100%' }}>
              Үргэлжлүүлэх
            </Button>
          </Flex>
        </Form>
      </div>
    </div>
  );
};

const Payment = ({
  items,
  addItem,
  goPaid,
}: {
  items: any[];
  addItem: () => void;
  goPaid: () => void;
}) => {
  const totalAmount = useMemo(() => {
    return items.reduce(
      (partialSum, item) => partialSum + item.deliveryCost + (item?.additional_cost || 0),
      0
    );
  }, [items]);

  return (
    <div style={{ background: '#FFF' }}>
      <MobileStep current={2} />
      <div style={{ padding: 24 }}>
        {(items || []).map((item, index) => {
          return (
            <div className="price" key={item.key}>
              <Row gutter={8}>
                <Col span={12}>
                  <h4>{item.itemType}</h4>
                </Col>
                <Col span={12} className="text-right">
                  <span>
                    {item.quantity} x {priceFormat(item.deliveryCost)}₮
                  </span>
                </Col>
              </Row>
              <Row gutter={8}>
                <Col span={24} className="text-left">
                  <span>
                    <b>Анхаарах :</b> {item?.warningName}
                  </span>
                  ,
                  <span>
                    <b>Хэмжээ :</b> {item?.weightTypeName}
                  </span>
                </Col>
              </Row>
              <Row gutter={8}>
                <Col span={24} className="text-right">
                  <span>
                    <b>Нийт үнэ :</b>{' '}
                    {priceFormat(
                      (item?.deliveryCost + item?.additionalCost || 0) * item?.quantity || 1
                    )}
                    ₮
                  </span>
                </Col>
              </Row>
              <Divider style={{ marginBottom: 15, marginTop: 15 }} />
              <Flex justify="center">
                <div>
                  <b>Нийт төлбөр: </b>
                  {priceFormat(totalAmount)}₮
                </div>
              </Flex>
              <Row gutter={8} style={{ marginTop: 15 }}>
                <Col span={24}>
                  <Flex gap={24}>
                    <Button onClick={() => addItem()} style={{ width: '100%' }} type="default">
                      Захиалга нэмэх
                    </Button>
                    <Button style={{ width: '100%' }} type="primary" onClick={() => goPaid()}>
                      Шууд төлөх
                    </Button>
                  </Flex>
                </Col>
              </Row>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MobileOrderCreate;
